
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit2, HelpCircle, Save, Trash2, X, ToggleLeft, ToggleRight } from "lucide-react";
import { FileItem } from "../files/types";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyTableMessage } from "../news/components/EmptyTableMessage";
import { qaCategories } from "./useQAManagement";

interface QATableProps {
  qaItems: FileItem[];
  editingItem: FileItem | null;
  isLoading?: boolean;
  onStartEdit: (item: FileItem) => void;
  onCancelEdit: () => void;
  onSaveEdit: (item: FileItem) => void;
  onDeleteQA: (id: number) => void;
  onToggleActive?: (id: number) => void;
}

export function QATable({
  qaItems,
  editingItem,
  isLoading = false,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDeleteQA,
  onToggleActive,
}: QATableProps) {
  const [editedQuestion, setEditedQuestion] = useState("");
  const [editedAnswer, setEditedAnswer] = useState("");
  const [editedCategory, setEditedCategory] = useState("");
  
  // Set initial values when editing starts
  useEffect(() => {
    if (editingItem) {
      setEditedQuestion(editingItem.question || "");
      setEditedAnswer(editingItem.answer || "");
      setEditedCategory(editingItem.category || "qa");
    }
  }, [editingItem]);
  
  const handleSaveEdit = () => {
    if (!editingItem) return;
    
    onSaveEdit({
      ...editingItem,
      question: editedQuestion,
      answer: editedAnswer,
      category: editedCategory,
      name: editedQuestion.substring(0, 50),
    });
  };

  // Helper function to get color based on category
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'documents':
        return 'bg-blue-500';
      case 'guides':
        return 'bg-green-500';
      case 'webinars':
        return 'bg-yellow-500';
      case 'videos':
        return 'bg-purple-500';
      case 'qa':
        return 'bg-pink-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Function to get category label
  const getCategoryLabel = (value: string): string => {
    const categories: Record<string, string> = {
      "documents": "Πρότυπες μελέτες",
      "videos": "Εκπαιδευτικά Video",
      "guides": "Goto Οδηγίες",
      "webinars": "Webinars",
      "qa": "Q&A"
    };
    
    return categories[value] || value;
  };

  return (
    <div>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
        </div>
      ) : qaItems.length === 0 ? (
        <EmptyTableMessage 
          colSpan={6} 
          message="Δεν υπάρχουν ερωτήσεις & απαντήσεις. Προσθέστε την πρώτη σας ερώτηση χρησιμοποιώντας τη φόρμα." 
        />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead>Ερώτηση</TableHead>
                <TableHead>Απάντηση</TableHead>
                <TableHead>Κατηγορία</TableHead>
                <TableHead>Κατάσταση</TableHead>
                <TableHead>Ημ/νία</TableHead>
                <TableHead className="text-right">Ενέργειες</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {qaItems.map((item) => (
                <TableRow key={item.id} className={!item.isActive ? "opacity-60" : ""}>
                  <TableCell><HelpCircle className="h-4 w-4" /></TableCell>
                  
                  <TableCell className="font-medium max-w-[250px]">
                    {editingItem && editingItem.id === item.id ? (
                      <Input 
                        value={editedQuestion}
                        onChange={(e) => setEditedQuestion(e.target.value)}
                        className="py-1 h-8"
                      />
                    ) : (
                      <div className="truncate">{item.question}</div>
                    )}
                  </TableCell>
                  
                  <TableCell className="max-w-[250px]">
                    {editingItem && editingItem.id === item.id ? (
                      <Textarea 
                        value={editedAnswer}
                        onChange={(e) => setEditedAnswer(e.target.value)}
                        className="py-1 min-h-[60px]"
                      />
                    ) : (
                      <div className="truncate">{item.answer}</div>
                    )}
                  </TableCell>
                  
                  <TableCell>
                    {editingItem && editingItem.id === item.id ? (
                      <Select value={editedCategory} onValueChange={setEditedCategory}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {qaCategories.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="secondary" className={`${getCategoryColor(item.category || '')} text-white`}>
                        {getCategoryLabel(item.category || '')}
                      </Badge>
                    )}
                  </TableCell>
                  
                  <TableCell>
                    <Badge variant={item.isActive ? "default" : "outline"}>
                      {item.isActive ? "Ενεργό" : "Ανενεργό"}
                    </Badge>
                  </TableCell>
                  
                  <TableCell>{item.uploadDate}</TableCell>
                  
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {editingItem && editingItem.id === item.id ? (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={handleSaveEdit}
                            disabled={!editedQuestion || !editedAnswer}
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={onCancelEdit}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => onStartEdit(item)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          
                          {onToggleActive && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => onToggleActive(item.id)}
                              title={item.isActive ? "Απενεργοποίηση" : "Ενεργοποίηση"}
                            >
                              {item.isActive ? (
                                <ToggleRight className="h-4 w-4" />
                              ) : (
                                <ToggleLeft className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => onDeleteQA(item.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
